/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    SPLIT_COMPANY_PROFILE_INFO
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Extracted company information section from CompanyProfileForm.tsx.
 * Fixed incorrect relative import paths and implicit any types.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/company-profile/components/CompanyInformation.tsx
 */
import React, { useRef, useState } from "react";
import { Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { resizeImages } from "../../../../utils/imageResizer";
import PhoneInput from "../../../ui/PhoneInput";
import { notify } from "../../../../utils/notifications";

export const CompanyInformation: React.FC<{ formik: any }> = ({ formik }) => {
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleLogoUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setIsProcessing(true);
    try {
      const resized = await resizeImages(Array.from(files), 500, 500, 0.8, 500);
      formik.setFieldValue("companyLogos", resized.slice(0, 1));
    } catch (e) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(null, "We couldn't process the logo. Please try again");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="mt-3">
      <div className="field-name mb-2">Upload Company Logo</div>
      <div className="upload-grid mb-3">
        {formik.values.companyLogos?.map((img: any, idx: number) => (
          <div key={idx} className="upload-logo-square upload-logo-square-relative">
            <img alt="Logo" src={img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`} className="upload-logo-image" />
            <button type="button" onClick={() => formik.setFieldValue("companyLogos", [])} className="upload-logo-remove-btn"><FontAwesomeIcon icon={faTimes} /></button>
          </div>
        ))}
        {(!formik.values.companyLogos || formik.values.companyLogos.length < 1) && (
          <div className="upload-logo-square" onClick={() => logoInputRef.current?.click()}>
            <FontAwesomeIcon icon={isProcessing ? faSpinner : faPlus} spin={isProcessing} />
          </div>
        )}
      </div>
      <input ref={logoInputRef} type="file" accept="image/*" className="d-none" onChange={(e) => handleLogoUpload(e.currentTarget.files)} />
      
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <Form.Label className="field-name mb-0">Company name</Form.Label>
          <span className="text-muted ms-1 small">(required for broadcasts)</span>
        </Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("companyName")} isInvalid={!!(formik.touched.companyName && formik.errors.companyName)} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Website</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("website")} isInvalid={!!(formik.touched.website && formik.errors.website)} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}><Form.Label className="field-name mb-0">Primary Email</Form.Label></Col>
        <Col md={8}><Form.Control className="field-input" {...formik.getFieldProps("companyEmail")} isInvalid={!!(formik.touched.companyEmail && formik.errors.companyEmail)} /></Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col md={4}>
          <Form.Label className="field-name mb-0">Tel</Form.Label>
          <span className="text-muted ms-1 small">(required for broadcasts)</span>
        </Col>
        <Col md={8}>
          {/* [2026-02-07] Pass country to sync phone input when company country changes */}
          <PhoneInput
            name="phone"
            value={formik.values.phone || ""}
            onChange={(val: string) => formik.setFieldValue("phone", val)}
            onBlur={formik.handleBlur}
            isInvalid={!!(formik.touched.phone && formik.errors.phone)}
            country={formik.values.country}
          />
        </Col>
      </Row>
    </div>
  );
};
